import { ColumnsType } from "antd/es/table";
import { Space, Tag, Typography } from "antd";
import { ExamPeriod, ExamPeriodMonitor, ExamPeriodMonitorState } from "@types";
import { ExamPeriodActivityTableActions } from "@components";
import { Translation } from "react-i18next";
import { TableProgressBar } from "../components/exam-period/monitor/TableProgressBar";
import { TableTimeLeft } from "../components/exam-period/monitor/TableTimeLeft";

export const examPeriodActivityTable = (examPeriod: ExamPeriod | undefined): ColumnsType<ExamPeriodMonitor> => [
    {
        title: <Translation> 
        {
            t => t("table.nameLastname")
        }
        </Translation>,
        dataIndex: "userExaminee",
        key: "participant",
        render(value) {
            return `${value.firstName} ${value.lastName}`
        }
    },
    {
        title: <Translation> 
        {
            t => t("table.status")
        }
        </Translation>,
        dataIndex: "state",
        key: "state",
        render(value: ExamPeriodMonitorState) {
            switch (value) {
                case ExamPeriodMonitorState.WaitingForParticipant:
                    return <Translation> 
                    {
                        t => <Typography.Text strong type="warning">{t("pages.examPeriods.waitingForParticipant")}</Typography.Text>
                    }
                    </Translation>
                case ExamPeriodMonitorState.NoShow:
                    return <Translation> 
                    {
                        t => <Typography.Text strong type="danger">{t("pages.examPeriods.notShowed")}</Typography.Text>
                    }
                    </Translation>
                case ExamPeriodMonitorState.InProgress:
                    return <Translation> 
                    {
                        t => <Typography.Text strong type="success">{t("pages.examPeriods.inProgress")}</Typography.Text>
                    }
                    </Translation>
                case ExamPeriodMonitorState.Completed:
                    return <Translation> 
                    {
                        t => <Typography.Text strong type="success">{t("pages.examPeriods.completed")}</Typography.Text>
                    }
                    </Translation>
                case ExamPeriodMonitorState.Excluded:
                    return <Translation> 
                    {
                        t => <Typography.Text strong type="danger">{t("pages.examPeriods.disqualified")}</Typography.Text>
                    }
                    </Translation>
                case ExamPeriodMonitorState.Paused:
                    return <Translation> 
                    {
                        t => <Typography.Text strong type="warning">{t("pages.examPeriods.paused")}</Typography.Text>
                    }
                    </Translation>
                case ExamPeriodMonitorState.ParticipantConfirmed:
                    return <Translation> 
                    {
                        t => <Typography.Text strong type="success">{t("pages.examPeriods.participantConfirmed")}</Typography.Text>
                    }
                    </Translation>
                default:
                    return "";
            }
        }
    },
    {
        title: <Translation> 
        {
            t => t("table.percentage")
        }
        </Translation>,
        key: "percentage",
        dataIndex: "percentage",
        render(_, record) {
            return <TableProgressBar examMonitor={record}/>
        },
    },
    {
        title: <Translation> 
        {
            t => t("table.timeLeft")
        }
        </Translation>,
        dataIndex: "timeLeft",
        key: "timeLeft",
        render(_, record) {
            return <TableTimeLeft examMonitor={record}/>
        }
    },
    {
        title: <Translation>
        {
            t => t("table.proctoring")
        }
        </Translation>,
        key: "proctoring",
        render(_, record) {
            return <Translation>
            {
                t => {
                    const tags = [];

                    if (record.requiresSafeExamBrowser) {
                        tags.push(
                            <Tag color={record.hasSafeExamBrowserViolation ? "error" : "success"} key="seb">
                                {record.hasSafeExamBrowserViolation ? t("pages.activity.safeExamBrowserMissing") : t("pages.activity.safeExamBrowserDetected")}
                            </Tag>
                        );
                    }

                    if (record.hasMultipleSessions) {
                        tags.push(
                            <Tag color="error" key="multiple-sessions">
                                {t("pages.activity.multipleSessions", { count: record.sessionCount })}
                            </Tag>
                        );
                    } else if (record.sessionCount > 0) {
                        tags.push(
                            <Tag color="default" key="session-count">
                                {t("pages.activity.sessionCount", { count: record.sessionCount })}
                            </Tag>
                        );
                    }

                    if (record.lastExitAt) {
                        tags.push(
                            <Tag color="warning" key="exit-detected">
                                {t("pages.activity.exitDetected")}
                            </Tag>
                        );
                    }

                    if (tags.length === 0) {
                        tags.push(
                            <Tag color="default" key="no-events">
                                {t("pages.activity.noProctoringAlerts")}
                            </Tag>
                        );
                    }

                    return <Space wrap>{tags}</Space>;
                }
            }
            </Translation>
        }
    },
    {
        title: <Translation> 
        {
            t => t("table.actions")
        }
        </Translation>,
        key: "actions",
        render(_, record) {
            return <ExamPeriodActivityTableActions examPeriod={examPeriod} examPeriodActivity={record}/>
        },
    },
    {
        title: <Translation> 
        {
            t => t("table.outcome")
        }
        </Translation>,
        dataIndex: "hasPassed",
        key: "outcome",
        render(value, record) {
            if (record?.state === ExamPeriodMonitorState.Completed || record.state === ExamPeriodMonitorState.Excluded) {
                return value ? <Translation> 
                {
                    t => <Typography.Text strong type="success">{t("table.passed")}</Typography.Text>
                }
                </Translation> : <Translation> 
                {
                    t => <Typography.Text strong type="danger">{t("table.failed")}</Typography.Text>
                }
                </Translation>
            }
        }
    },
]